import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import SlotCalendar from './SlotCalendar'
import TimeSlot from './TimeSlot'
import {
  ModalOverlay,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  ModalBody,
  AvailableDatesSection,
  SectionHeader,
  SectionTitle,
  DateDisplay,
  SlotsGrid,
  NoSlotsMessage,
  NoSlotsText,
  NoSlotsSubtext,
} from './styled'
import X from '../../assets/svgs/X'

function SlotModal({
  isOpen,
  onClose,
  availableDates,
  onSelectSlot,
  initialSelectedDate,
}) {
  const { t, i18n } = useTranslation()
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedDaySlots, setSelectedDaySlots] = useState(null)

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') onClose()
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey)

      if (availableDates?.length > 0) {
        let dateToSelect = null

        if (initialSelectedDate) {
          const matchingDate = availableDates.find(
            (day) =>
              day.date.toDateString() === initialSelectedDate.toDateString() &&
              day.slots.some((slot) => slot.isAvailable),
          )
          if (matchingDate) dateToSelect = matchingDate.date
        }

        if (!dateToSelect) {
          const firstAvailableDate = availableDates.find((day) =>
            day.slots.some((slot) => slot.isAvailable),
          )
          if (firstAvailableDate) dateToSelect = firstAvailableDate.date
        }

        if (dateToSelect) setSelectedDate(dateToSelect)
      }
    }

    return () => document.removeEventListener('keydown', handleEscKey)
  }, [isOpen, onClose, availableDates, initialSelectedDate])

  useEffect(() => {
    if (selectedDate) {
      const daySlots = availableDates.find(
        (day) => day.date.toDateString() === selectedDate.toDateString(),
      )
      setSelectedDaySlots(daySlots || null)
    } else {
      setSelectedDaySlots(null)
    }
  }, [selectedDate, availableDates])

  const handleSelectDate = (date) => setSelectedDate(date)

  const handleSelectTimeSlot = (slot) => {
    if (selectedDate)
      onSelectSlot(selectedDate, slot.startTime, slot.endTime, slot)
  }

  const formatSelectedDate = (date) => {
    if (!date) return ''
    const options = { weekday: 'long', month: 'long', day: 'numeric' }
    try {
      const locale = t('calendar.locale')
      return date.toLocaleDateString(locale, options)
    } catch {
      try {
        return date.toLocaleDateString(i18n.language, options)
      } catch {
        return date.toLocaleDateString(undefined, options)
      }
    }
  }

  if (!isOpen) return null

  return (
    <ModalOverlay>
      <ModalBackdrop onClick={onClose} />
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <ModalTitle>{t('calendar.chooseInterviewSlot')}</ModalTitle>
            <ModalSubtitle>{t('calendar.selectDateTimeWorks')}</ModalSubtitle>
          </div>
          <CloseButton onClick={onClose}>
            <X width={24} height={24} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          <SlotCalendar
            availableDates={availableDates}
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
          />
          {selectedDaySlots && (
            <AvailableDatesSection>
              <SectionHeader>
                <SectionTitle>{t('calendar.availableTimes')}</SectionTitle>
                <DateDisplay>{formatSelectedDate(selectedDate)}</DateDisplay>
              </SectionHeader>
              {selectedDaySlots.slots.length > 0 ? (
                <SlotsGrid>
                  {selectedDaySlots.slots.map((slot, index) => (
                    <TimeSlot
                      key={`${slot.id || index}`}
                      startTime={slot.startTime}
                      isAvailable={slot.isAvailable}
                      isBooked={slot.originalData?.isBooked || false}
                      onClick={() =>
                        slot.isAvailable && handleSelectTimeSlot(slot)
                      }
                    />
                  ))}
                </SlotsGrid>
              ) : (
                <NoSlotsMessage>
                  <NoSlotsText>{t('calendar.noSlotsForDate')}</NoSlotsText>
                  <NoSlotsSubtext>
                    {t('calendar.selectAnotherDate')}
                  </NoSlotsSubtext>
                </NoSlotsMessage>
              )}
            </AvailableDatesSection>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  )
}

SlotModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  availableDates: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.instanceOf(Date).isRequired,
      isAvailable: PropTypes.bool.isRequired,
      slots: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          startTime: PropTypes.string.isRequired,
          endTime: PropTypes.string.isRequired,
          isAvailable: PropTypes.bool.isRequired,
          // eslint-disable-next-line react/forbid-prop-types
          originalData: PropTypes.any,
          eventId: PropTypes.string,
          assignedUser: PropTypes.string,
        }),
      ).isRequired,
    }),
  ).isRequired,
  onSelectSlot: PropTypes.func.isRequired,
  initialSelectedDate: PropTypes.instanceOf(Date),
}

export default SlotModal
